/** Versions as a timeline: who made and published each, compare any two in sentences, restore one into the draft. */

import { useState } from "react";
import { GitCompare, Layers } from "lucide-react";

import {
  Banner,
  Button,
  Card,
  ChangeList,
  Column,
  Field,
  FieldRow,
  Inline,
  Panel,
  Select,
  SkeletonRows,
  Timeline,
  TimelineItem,
  TimelineMain,
  TimelineSub,
  TonePill,
} from "@/components/kit";
import { getFields, listVersions, restoreVersion, type IntegrationDetail } from "@/lib/integrations/api";
import { compareSettings } from "@/lib/integrations/compare";
import { normaliseSettings } from "@/lib/integrations/settings";
import { when } from "@/lib/integrations/words";
import { errorText } from "@/spa/useApi";

import { useKeyed } from "../hooks";

const STATUS: Record<string, { word: string; tone: "good" | "info" | "neutral" }> = {
  live: { word: "Live", tone: "good" },
  draft: { word: "Draft", tone: "info" },
  archived: { word: "Earlier", tone: "neutral" },
};

export function HistoryTab({ item, canWrite, onChanged }: { item: IntegrationDetail; canWrite: boolean; onChanged: () => void }) {
  const timeZone = item.eventTimezone ?? "UTC";
  const versions = useKeyed(`versions|${item.code}|${item.liveVersion}|${item.draftVersion}`, () => listVersions(item.code));
  const fields = useKeyed(`fields|${item.code}`, () => getFields(item.code));
  const [from, setFrom] = useState<number | null>(null);
  const [to, setTo] = useState<number | null>(null);
  const [asking, setAsking] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: "good" | "danger"; text: string } | null>(null);

  if (!versions.data) {
    return versions.error ? <Banner tone="danger" actions={<Button onClick={versions.reload}>Try again</Button>}>{versions.error}</Banner> : <SkeletonRows rows={4} columns={5} />;
  }
  const list = versions.data;
  const newest = list[0]?.number ?? null;
  const older = list[1]?.number ?? newest;
  const a = list.find((one) => one.number === (from ?? older));
  const b = list.find((one) => one.number === (to ?? newest));
  const labels = Object.fromEntries((fields.data?.fields ?? []).map((field) => [field.key, field.label]));
  const changes = a && b ? compareSettings(normaliseSettings(a.settings), normaliseSettings(b.settings), labels) : [];

  const restore = (number: number) => {
    setBusy(true);
    restoreVersion(item.code, number)
      .then(
        (updated) => {
          setSaid({ tone: "good", text: `Version ${number} was copied into draft ${updated.draftVersion}. Publish it on the Settings tab to make it live.` });
          versions.reload();
          onChanged();
        },
        (thrown) => setSaid({ tone: "danger", text: errorText(thrown, "omnium did not restore that version") }),
      )
      .finally(() => {
        setBusy(false);
        setAsking(null);
      });
  };

  return (
    <Column>
      <Panel
        title="Versions"
        icon={<Layers size={18} aria-hidden />}
        note="Every saved change is a version. Drafts never run; the live one does. Earlier versions are kept."
      >
        {said && <Banner tone={said.tone}>{said.text}</Banner>}
        {asking !== null && (
          <Banner
            tone="warning"
            actions={
              <>
                <Button tone="primary" disabled={busy} onClick={() => restore(asking)}>
                  {busy ? "Restoring…" : `Copy version ${asking} into the draft`}
                </Button>
                <Button tone="subtle" disabled={busy} onClick={() => setAsking(null)}>
                  Cancel
                </Button>
              </>
            }
          >
            {item.draftVersion !== null
              ? `This replaces what is in draft ${item.draftVersion}. The live version keeps running until you publish.`
              : "This makes a new draft. The live version keeps running until you publish."}
          </Banner>
        )}
        <Timeline label="Versions, newest first">
          {list.map((version) => {
            const state = STATUS[version.status] ?? { word: version.status, tone: "neutral" as const };
            return (
              <TimelineItem
                key={version.number}
                compact
                tone={state.tone}
                icon={<strong aria-hidden>{version.number}</strong>}
                cells={[
                  {
                    key: "about",
                    content: (
                      <>
                        <Inline>
                          <TimelineMain>Version {version.number}</TimelineMain>
                          <TonePill tone={state.tone}>{state.word}</TonePill>
                        </Inline>
                        <TimelineSub>
                          Made {when(version.createdAt, timeZone)}
                          {version.createdBy ? ` by ${version.createdBy}` : ""}
                          {version.publishedAt
                            ? ` · published ${when(version.publishedAt, timeZone)}${version.publishedBy ? ` by ${version.publishedBy}` : ""}`
                            : " · never published"}
                        </TimelineSub>
                        {version.note && <span style={{ overflowWrap: "anywhere" }}>{version.note}</span>}
                      </>
                    ),
                  },
                ]}
                actions={
                  version.status !== "draft" ? (
                    <Button tone="subtle" disabled={!canWrite || busy} onClick={() => setAsking(version.number)}>
                      Restore
                    </Button>
                  ) : null
                }
              />
            );
          })}
        </Timeline>
      </Panel>

      {list.length > 1 && (
        <Panel title="Compare" icon={<GitCompare size={18} aria-hidden />} note="What changed from one version to another, in sentences.">
          <FieldRow>
            <Field label="From version">
              <Select value={a?.number ?? ""} onChange={(event) => setFrom(Number(event.target.value))}>
                {list.map((one) => (
                  <option key={one.number} value={one.number}>
                    {one.number} ({STATUS[one.status]?.word ?? one.status})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="To version">
              <Select value={b?.number ?? ""} onChange={(event) => setTo(Number(event.target.value))}>
                {list.map((one) => (
                  <option key={one.number} value={one.number}>
                    {one.number} ({STATUS[one.status]?.word ?? one.status})
                  </option>
                ))}
              </Select>
            </Field>
          </FieldRow>
          <Card>
            <ChangeList changes={changes} empty={a?.number === b?.number ? "Pick two different versions." : "These two versions have the same settings."} />
          </Card>
        </Panel>
      )}
    </Column>
  );
}
